/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Wallet, ChartLineUp, Sparkle, ShieldCheck, CalendarBlank, Lightning,
  Brain, ChatCircleDots, Megaphone, Eye, ArrowDown, CurrencyCircleDollar,
  TrendUp, Info,
} from "@phosphor-icons/react";

/**
 * Financial — premium operational-credits dashboard.
 *
 * Voice & wording follow the operator's brief:
 *   - "Créditos operacionais"
 *   - "Franquia operacional"
 *   - "Saldo disponível"
 *   - NEVER "tokens", "custo de IA", "cobrança por mensagem"
 *
 * Stripe is deliberately NOT wired in this phase. The purchase CTAs are
 * present but disabled with an "Em breve" badge so the layout & flow are
 * already validated by the operator before Phase 3.
 */

const ACTION_VISUALS = {
  "ai.orchestrator":   { label: "Atendimento inteligente", icon: Brain,            tone: "violet" },
  "ai.fast_path":      { label: "Respostas rápidas",       icon: Sparkle,          tone: "amber" },
  "ai.vehicle_vision": { label: "Leitura de documentos",   icon: Eye,              tone: "blue" },
  "ai.audio_whisper":  { label: "Áudio do cliente",        icon: ChatCircleDots,   tone: "slate" },
  "message.outbound":  { label: "Mensagens enviadas",      icon: ChatCircleDots,   tone: "emerald" },
  "campaign.send":     { label: "Campanhas",               icon: Megaphone,        tone: "indigo" },
  "decision.follow_up":{ label: "Acompanhamentos",         icon: Lightning,        tone: "orange" },
};

const TONE_BG = {
  violet:  "bg-violet-50 text-violet-700",
  amber:   "bg-amber-50 text-amber-700",
  blue:    "bg-blue-50 text-blue-700",
  slate:   "bg-slate-100 text-slate-700",
  emerald: "bg-emerald-50 text-emerald-700",
  indigo:  "bg-indigo-50 text-indigo-700",
  orange:  "bg-orange-50 text-orange-700",
};

const PACKAGES = [
  { code: "pack_5k",  credits: 5000,  price_cents:  8900, label: "+5.000 créditos",  blurb: "Para campanhas pontuais" },
  { code: "pack_20k", credits: 20000, price_cents: 29700, label: "+20.000 créditos", blurb: "Mais popular",        highlight: true },
  { code: "pack_50k", credits: 50000, price_cents: 69700, label: "+50.000 créditos", blurb: "Volume operacional" },
];

function formatBRL(cents) {
  return (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });
}

function fmtDateBR(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

function fmtDateTimeBR(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function daysUntilRenewal(periodStartedAt) {
  if (!periodStartedAt) return null;
  try {
    const start = new Date(periodStartedAt);
    const end = new Date(start);
    end.setDate(end.getDate() + 30);
    const diff = Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  } catch { return null; }
}

function projectExhaustion(wallet, totalConsumedLast30d) {
  // Forecast based on the last-30d average. Returns days remaining or null.
  if (!wallet || totalConsumedLast30d <= 0) return null;
  const perDay = totalConsumedLast30d / 30;
  if (perDay <= 0 || !wallet.balance) return null;
  return Math.round(wallet.balance / perDay);
}

// ─── small composables ─────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sublabel, tone = "slate", testid }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-md p-4 hover:shadow-sm transition-shadow"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium">{label}</div>
          <div className="text-xl font-semibold text-slate-900 mt-1 tabular-nums">{value}</div>
          {sublabel && <div className="text-[11px] text-slate-500 mt-1">{sublabel}</div>}
        </div>
        <span className={`w-8 h-8 rounded-md flex items-center justify-center ${TONE_BG[tone]}`}>
          <Icon size={16} weight="regular" />
        </span>
      </div>
    </div>
  );
}

function ProgressBar({ percent, threshold }) {
  const color =
    threshold === "critical" ? "bg-rose-500"
      : threshold === "warn90" ? "bg-orange-500"
        : threshold === "warn80" ? "bg-amber-500"
          : "bg-emerald-500";
  return (
    <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} transition-all duration-700 ease-out`}
        style={{ width: `${Math.min(100, percent)}%` }}
        data-testid="financial-progress-bar"
      />
    </div>
  );
}

function ThresholdBadge({ threshold }) {
  const conf = {
    ok:       { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", text: "Operação saudável" },
    warn80:   { cls: "bg-amber-50 text-amber-700 border-amber-200",      text: "Atenção · 80% usado" },
    warn90:   { cls: "bg-orange-50 text-orange-700 border-orange-200",   text: "Crítico · 90% usado" },
    critical: { cls: "bg-rose-50 text-rose-700 border-rose-200",          text: "Franquia esgotada" },
  }[threshold || "ok"];
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border ${conf.cls}`}
      data-testid={`financial-threshold-badge-${threshold || "ok"}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {conf.text}
    </span>
  );
}

// ─── main page ─────────────────────────────────────────────────────

export default function Financial() {
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  // Custom credits — guarda como STRING para o usuário poder esvaziar
  // completamente o input (caso contrário, parseInt(0) faz o campo
  // ficar com "0" travado e qualquer dígito vira "01", "05" etc.).
  // Convertemos pra number só no momento do cálculo/compra.
  const [customAmount, setCustomAmount] = useState("10000");
  const [buying, setBuying] = useState(null);   // pack_code currently buying

  const buyPack = async (packCode) => {
    if (buying) return;
    setBuying(packCode);
    try {
      const { data } = await api.post("/payments/buy-pack", {
        pack_code: packCode,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não consegui iniciar a compra. Tente novamente.");
      setBuying(null);
    }
  };

  const buyCustom = async () => {
    if (buying) return;
    const parsed = parseInt(customAmount || "0", 10);
    const q = Math.max(1000, Math.min(200000, parsed || 0));
    if (q < 1000) {
      toast.error("Quantidade mínima: 1.000 créditos.");
      return;
    }
    setBuying("custom");
    try {
      const { data } = await api.post("/payments/buy-custom", {
        credits: q,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não consegui iniciar a compra. Tente novamente.");
      setBuying(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [w, l, u] = await Promise.all([
        api.get("/credits/wallet"),
        api.get("/credits/ledger?limit=20"),
        api.get("/credits/usage_summary?days=30"),
      ]);
      setWallet(w.data);
      setLedger(l.data || []);
      setUsage(u.data || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // After returning from Stripe (pack purchase), poll status until paid
  // and refresh wallet so the new balance shows up immediately.
  useEffect(() => {
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get("session_id");
    const cancelled = url.searchParams.get("cancelled");
    if (cancelled) {
      toast.info("Compra cancelada.");
      url.searchParams.delete("cancelled");
      window.history.replaceState({}, "", url.toString());
      return;
    }
    if (!sessionId) return;

    let stopped = false;
    let count = 0;
    const poll = async () => {
      if (stopped) return;
      count++;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (data.status === "paid") {
          toast.success(`+${(data.credits_granted || 0).toLocaleString("pt-BR")} créditos adicionados!`);
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.toString());
          load();
          return;
        }
        if (data.status === "failed" || data.status === "expired" || count > 20) {
          toast.error("Não conseguimos confirmar o pagamento.");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (count <= 20) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { stopped = true; };
  }, []);

  const renewalDays = daysUntilRenewal(wallet?.period_started_at);
  const totalConsumed = usage?.total_consumed || 0;
  const projectedDays = useMemo(() => projectExhaustion(wallet, totalConsumed), [wallet, totalConsumed]);

  // Custom-pack pricing — espelha EXATAMENTE a função `custom_pack_price_cents`
  // do backend (server é source-of-truth, mas calcular local evita um round-trip
  // a cada digit do input). Mantenha as duas em sync.
  const customPriceCents = useMemo(() => {
    const parsed = parseInt(customAmount || "0", 10);
    const q = Math.max(1000, Math.min(200000, parsed || 0));
    const unit = q >= 60000 ? 1.50 : q >= 30000 ? 1.60 : q >= 10000 ? 1.75 : 1.94;
    const raw = q * unit;
    // Arredonda para cima ao real inteiro mais próximo (sync com backend).
    return Math.ceil(raw / 100) * 100;
  }, [customAmount]);

  return (
    <>
      <Topbar
        title="Financeiro"
        subtitle="Franquia operacional inteligente — créditos consumidos por cada ação automatizada do AgentUS."
      />
      <div className="p-6 md:p-8 space-y-6" data-testid="financial-page">

        {/* ── HERO: saldo + progresso ─────────────────────────────── */}
        <section
          className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white rounded-lg p-6 md:p-8 shadow-sm relative overflow-hidden"
          data-testid="financial-hero"
        >
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
               style={{ backgroundImage: "radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-end">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-medium mb-3">
                <Wallet size={11} /> Saldo disponível
              </div>
              <div className="flex items-baseline gap-3 mb-4">
                <div className="text-5xl md:text-6xl font-semibold tabular-nums tracking-tight" data-testid="financial-balance-value">
                  {loading ? "—" : (wallet?.balance ?? 0).toLocaleString("pt-BR")}
                </div>
                <div className="text-slate-400 text-sm">créditos operacionais</div>
              </div>
              <ProgressBar percent={wallet?.percent_used || 0} threshold={wallet?.threshold} />

              {/* Three-bucket breakdown — explica de onde vem o saldo
                  total e qual parcela vai expirar no próximo ciclo.
                  Mostra apenas as buckets com saldo > 0 pra não poluir
                  a UI de tenants que só têm bônus / só têm mensal. */}
              <div className="mt-3 grid grid-cols-3 gap-2 text-[12px]" data-testid="wallet-buckets">
                <div className="rounded-lg border border-emerald-400/20 bg-emerald-400/5 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-0.5">
                    Créditos do mês
                  </div>
                  <div className="font-heading text-lg font-semibold text-white tabular-nums">
                    {(wallet?.monthly_balance ?? 0).toLocaleString("pt-BR")}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    expira na renovação
                  </div>
                </div>
                <div className="rounded-lg border border-amber-300/20 bg-amber-300/5 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-0.5">
                    Bônus
                  </div>
                  <div className="font-heading text-lg font-semibold text-white tabular-nums">
                    {(wallet?.bonus_balance ?? 0).toLocaleString("pt-BR")}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    cortesia · sem prazo
                  </div>
                </div>
                <div className="rounded-lg border border-sky-400/20 bg-sky-400/5 p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-sky-300 mb-0.5">
                    Pacotes extras
                  </div>
                  <div className="font-heading text-lg font-semibold text-white tabular-nums">
                    {(wallet?.pack_balance ?? 0).toLocaleString("pt-BR")}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    avulso · sem prazo
                  </div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px] text-slate-300">
                <span className="tabular-nums">
                  {(wallet?.period_used ?? 0).toLocaleString("pt-BR")} usados de{" "}
                  {(wallet?.period_total ?? wallet?.monthly_grant ?? 0).toLocaleString("pt-BR")}
                  {" "}este mês
                </span>
                <span className="opacity-40">•</span>
                <span className="tabular-nums">
                  {wallet?.percent_used != null ? `${Math.round(wallet.percent_used)}%` : "—"} consumido
                </span>
                {projectedDays != null && (
                  <>
                    <span className="opacity-40">•</span>
                    <span className="inline-flex items-center gap-1">
                      <TrendUp size={12} />
                      <span className="tabular-nums">
                        ~{projectedDays}d para zerar no ritmo atual
                      </span>
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-3">
              <ThresholdBadge threshold={wallet?.threshold} />
              {!wallet?.enforcement_enabled && (
                <span
                  className="text-[10px] uppercase tracking-[0.16em] text-slate-400 bg-slate-800/60 border border-slate-700 rounded-full px-2.5 py-1"
                  title="A operação continua livre. Estamos calibrando seu consumo antes da cobrança."
                  data-testid="financial-shadow-pill"
                >
                  modo observação
                </span>
              )}
            </div>
          </div>
        </section>

        {/* ── CARDS SECUNDÁRIOS ───────────────────────────────────── */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="financial-stats-grid">
          <StatCard
            icon={ShieldCheck} tone="emerald"
            label="Plano atual"
            value={
              wallet?.plan_code === "essential" ? "Essencial"
              : wallet?.plan_code === "base" ? "Enterprise"
              : (wallet?.plan_code || "—")
            }
            sublabel={`Franquia ${(wallet?.monthly_grant ?? 0).toLocaleString("pt-BR")}/mês`}
            testid="financial-stat-plan"
          />
          <StatCard
            icon={CalendarBlank} tone="blue"
            label="Próxima renovação"
            value={renewalDays != null ? `${renewalDays}d` : "—"}
            sublabel={renewalDays === 0 ? "Hoje" : "+ créditos automaticamente"}
            testid="financial-stat-renewal"
          />
          <StatCard
            icon={ChartLineUp} tone="violet"
            label="Consumo este mês"
            value={(wallet?.period_used ?? 0).toLocaleString("pt-BR")}
            sublabel="créditos · ciclo atual"
            testid="financial-stat-month-usage"
          />
          <StatCard
            icon={Sparkle} tone="amber"
            label="Créditos extras"
            value={"0"}
            sublabel="Compre quando precisar"
            testid="financial-stat-extras"
          />
        </section>

        {/* ── USO POR TIPO ────────────────────────────────────────── */}
        <section className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6" data-testid="financial-usage-section">
          <div className="bg-white border border-slate-200 rounded-md">
            <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold tracking-tight">Onde a operação consumiu</h3>
                <p className="text-[12px] text-slate-500 mt-0.5">Últimos 30 dias · todas as ações automatizadas</p>
              </div>
              <span className="text-[11px] text-slate-400 tabular-nums">{totalConsumed.toLocaleString("pt-BR")} total</span>
            </header>
            <div className="p-5">
              {(usage?.by_action || []).length === 0 ? (
                <EmptyState
                  title="Ainda sem consumo registrado"
                  desc="Conforme o AgentUS conversar com clientes, você verá aqui exatamente onde sua franquia foi aplicada."
                />
              ) : (
                <ul className="space-y-3" data-testid="financial-usage-list">
                  {(usage.by_action || []).map((row) => {
                    const conf = ACTION_VISUALS[row.action_code] || { label: row.action_code, icon: Lightning, tone: "slate" };
                    const Icon = conf.icon;
                    const pct = totalConsumed ? Math.round((row.credits / totalConsumed) * 100) : 0;
                    return (
                      <li key={row.action_code} className="flex items-center gap-4">
                        <span className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${TONE_BG[conf.tone]}`}>
                          <Icon size={16} weight="regular" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2 mb-1">
                            <span className="text-sm font-medium text-slate-900 truncate">{conf.label}</span>
                            <span className="text-[12px] text-slate-500 tabular-nums shrink-0">
                              {row.credits.toLocaleString("pt-BR")} · {pct}%
                            </span>
                          </div>
                          <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-slate-700 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="text-[11px] text-slate-400 mt-1 tabular-nums">{row.events} ocorrências</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* HISTÓRICO RECENTE */}
          <div className="bg-white border border-slate-200 rounded-md flex flex-col">
            <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold tracking-tight">Histórico recente</h3>
                <p className="text-[12px] text-slate-500 mt-0.5">Movimentações auditadas da franquia</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-1 rounded-full font-medium">
                live
              </span>
            </header>
            <ul className="divide-y divide-slate-100 overflow-y-auto max-h-[420px]" data-testid="financial-ledger-list">
              {ledger.length === 0 && (
                <li className="p-6">
                  <EmptyState
                    title="Sem movimentações ainda"
                    desc="Cada ação do seu AgentUS é registrada aqui para você acompanhar."
                  />
                </li>
              )}
              {ledger.map((tx) => {
                const isOut = tx.kind === "consume";
                const isGrant = tx.kind === "grant" || tx.kind === "purchase";
                const sign = tx.amount > 0 ? "+" : "";
                const amountCls = isOut ? "text-slate-600" : isGrant ? "text-emerald-700" : "text-slate-700";
                const label = tx.kind === "consume"
                  ? (ACTION_VISUALS[tx.action_code]?.label || tx.action_code || "Consumo")
                  : tx.kind === "grant" && tx.source === "initial_setup" ? "Franquia inicial"
                  : tx.kind === "grant" && tx.source === "plan_renewal" ? "Renovação do plano"
                  : tx.kind === "grant" ? "Bônus operacional"
                  : tx.kind === "purchase" ? "Compra de créditos"
                  : tx.kind === "refund" ? "Estorno"
                  : tx.kind === "adjust" ? "Ajuste manual"
                  : tx.kind;
                return (
                  <li key={tx.id} className="px-5 py-3 flex items-center gap-3 text-sm hover:bg-slate-50/60 transition-colors">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                      isOut ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-600"
                    }`}>
                      {isOut ? <ArrowDown size={12} weight="bold" /> : <Sparkle size={12} weight="fill" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{label}</div>
                      <div className="text-[11px] text-slate-500">{fmtDateTimeBR(tx.created_at)}</div>
                    </div>
                    <div className={`text-sm font-semibold tabular-nums ${amountCls}`}>
                      {sign}{tx.amount.toLocaleString("pt-BR")}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* ── COMPRAR CRÉDITOS (Stripe vem depois) ──────────────── */}
        <section className="bg-white border border-slate-200 rounded-md" data-testid="financial-purchase-section">
          <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-heading font-semibold tracking-tight">Reforçar sua franquia</h3>
              <p className="text-[12px] text-slate-500 mt-0.5">
                Adicione créditos extras a qualquer momento — eles entram instantaneamente na sua operação.
              </p>
            </div>
          </header>
          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            {PACKAGES.map((pkg) => (
              <article
                key={pkg.code}
                data-testid={`financial-package-${pkg.code}`}
                className={`relative rounded-lg p-5 transition-all hover:shadow-sm flex flex-col gap-3 ${
                  pkg.highlight
                    ? "bg-gradient-to-b from-slate-900 to-slate-800 text-white"
                    : "bg-slate-50 border border-slate-200 text-slate-900"
                }`}
              >
                {pkg.highlight && (
                  <span className="absolute -top-2 left-5 text-[10px] uppercase tracking-wider bg-amber-400 text-amber-900 px-2 py-0.5 rounded-full font-bold">
                    {pkg.blurb}
                  </span>
                )}
                <div className={`text-[10px] uppercase tracking-[0.18em] font-medium ${pkg.highlight ? "text-slate-400" : "text-slate-500"}`}>
                  Pacote
                </div>
                <div className="text-2xl font-semibold tabular-nums">{pkg.label}</div>
                <div className={`text-sm ${pkg.highlight ? "text-slate-300" : "text-slate-500"}`}>
                  {!pkg.highlight && pkg.blurb}
                </div>
                <div className="flex items-baseline gap-1 mt-2">
                  <span className={`text-3xl font-bold tabular-nums ${pkg.highlight ? "text-white" : "text-slate-900"}`}>
                    {formatBRL(pkg.price_cents)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => buyPack(pkg.code)}
                  disabled={buying === pkg.code}
                  data-testid={`financial-buy-${pkg.code}`}
                  className={`mt-2 rounded-md py-2.5 text-sm font-semibold transition-colors ${
                    pkg.highlight
                      ? "bg-white text-slate-900 hover:bg-slate-100"
                      : "bg-slate-900 text-white hover:bg-slate-800"
                  } disabled:opacity-60 disabled:cursor-wait`}
                >
                  {buying === pkg.code ? "Abrindo pagamento…" : "Comprar créditos"}
                </button>
              </article>
            ))}
          </div>

          {/* Custom amount */}
          <div className="px-5 pb-6 border-t border-slate-100 pt-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium mb-3">
              Quantidade personalizada
            </div>
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <label className="flex-1">
                <div className="text-[11px] text-slate-500 mb-1">Quantos créditos você quer adicionar?</div>
                <input
                  type="number"
                  min={1000}
                  max={200000}
                  step={1000}
                  inputMode="numeric"
                  value={customAmount}
                  onChange={(e) => {
                    // Aceita string crua + remove zeros à esquerda. Permite
                    // o input ficar vazio enquanto a pessoa digita (sem o
                    // "0" preso). Validação real acontece no clique do botão.
                    const raw = e.target.value.replace(/^0+(?=\d)/, "");
                    setCustomAmount(raw);
                  }}
                  onBlur={() => {
                    // Ao sair do campo, se ficar vazio, restaura o mínimo —
                    // mas só pra não exibir preview "R$ 19,40" travado em 1k.
                    if (!customAmount) setCustomAmount("1000");
                  }}
                  data-testid="financial-custom-credits-input"
                  className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5 text-sm tabular-nums focus:outline-none focus:border-slate-400"
                />
              </label>
              <div className="md:w-48 px-4 py-2 bg-slate-50 rounded-md border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Valor</div>
                <div className="text-lg font-semibold text-slate-900 tabular-nums" data-testid="financial-custom-credits-price">
                  {formatBRL(customPriceCents)}
                </div>
              </div>
              <button
                type="button"
                onClick={buyCustom}
                disabled={buying === "custom" || parseInt(customAmount || "0", 10) < 1000}
                data-testid="financial-buy-custom"
                className="md:w-48 bg-slate-900 text-white rounded-md py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {buying === "custom" ? "Abrindo pagamento…" : "Comprar personalizado"}
              </button>
            </div>
          </div>
        </section>

        {/* ── EXPLICAÇÃO PREMIUM (anti-FUD) ───────────────────────── */}
        <section className="bg-slate-50 border border-slate-200 rounded-md p-5 flex gap-4" data-testid="financial-explainer">
          <span className="w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-700 shrink-0">
            <CurrencyCircleDollar size={18} />
          </span>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">Como funciona sua franquia operacional</h4>
            <p className="text-[13px] text-slate-600 leading-relaxed">
              Cada ação que o AgentUS executa por você — responder no WhatsApp, ler um documento, disparar uma campanha
              — consome uma fração da sua franquia mensal. Você acompanha tudo aqui, com transparência total. Quando
              precisar de mais espaço, é só reforçar. <strong className="text-slate-800">Sem mensalidade por mensagem,
              sem surpresas, sem letra miúda.</strong>
            </p>
          </div>
        </section>
      </div>
    </>
  );
}

function EmptyState({ title, desc }) {
  return (
    <div className="text-center py-8" data-testid="financial-empty-state">
      <div className="w-10 h-10 rounded-full bg-slate-50 mx-auto mb-3 flex items-center justify-center text-slate-400">
        <Wallet size={18} />
      </div>
      <div className="text-sm font-medium text-slate-700 mb-1">{title}</div>
      <div className="text-[12px] text-slate-500 max-w-[280px] mx-auto leading-relaxed">{desc}</div>
    </div>
  );
}
